/**
 * 底部图标图表工具
 * 用于根据配置生成 ECharts 图表选项
 */

/**
 * 获取X轴数据
 * @param {Array} tableData 表格数据
 * @param {String} labelField X轴字段名
 * @returns {Array} 唯一的X轴数据
 */
export const getXAxisData = (tableData, labelField = 'projectName') => {
  return [...new Set(tableData.map(item => item[labelField]))];
};

/**
 * 创建基础图表配置
 * @param {Object} iconConfig 图标配置
 * @param {Object} column 列信息
 * @returns {Object} 基础图表选项
 */
export const createBaseOption = (iconConfig, column) => {
  return {
    title: {
      text: iconConfig.chartTitle || `${column.label}统计`,
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        return params.map(p => `${p.marker} ${p.seriesName}: ${p.value}`)
          .join('<br/>');
      }
    },
    legend: {
      data: [],
      bottom: 10
    },
    grid: {
      top: '80px',
      left: '3%',
      right: '3%',
      bottom: '30px',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: [] // 将在调用后填充
    },
    yAxis: {
      type: 'value',
      name: '数值'
    },
    series: []
  };
};

/**
 * 计算系列数据
 * @param {Array} tableData 表格数据
 * @param {String} xProp X轴字段名
 * @param {String} dataProp 数据字段名
 * @param {Array} xData X轴数据
 * @returns {Array} 系列数据数组
 */
export const calculateSeriesData = (tableData, xProp, dataProp, xData) => {
  const seriesData = new Array(xData.length).fill(0);

  tableData.forEach(item => {
    const xValue = item[xProp];
    const dataIndex = xData.indexOf(xValue);
    if (dataIndex !== -1) {
      // 修复：确保正确处理数值类型
      const value = Number(item[dataProp]);
      seriesData[dataIndex] += isNaN(value) ? 0 : value;
    }
  });

  return seriesData;
};

/**
 * 计算差额系列数据
 * @param {Array} tableData 表格数据
 * @param {String} xProp X轴字段名
 * @param {String} prop1 第一个数据字段名
 * @param {String} prop2 第二个数据字段名
 * @param {Array} xData X轴数据
 * @returns {Array} 差额系列数据数组
 */
export const calculateDifferenceSeriesData = (tableData, xProp, prop1, prop2, xData) => {
  const seriesData = new Array(xData.length).fill(0);

  tableData.forEach(item => {
    const xValue = item[xProp];
    const dataIndex = xData.indexOf(xValue);
    if (dataIndex !== -1) {
      const value1 = Number(item[prop1]) || 0;
      const value2 = Number(item[prop2]) || 0;
      const difference = value1 - value2;
      seriesData[dataIndex] += difference;
    }
  });

  return seriesData;
};

/**
 * 处理单柱图
 * @param {Object} option 图表选项
 * @param {Object} chartConfig 图表配置
 * @param {Object} iconConfig 图标配置
 * @param {Object} column 列信息
 * @param {Array} xData X轴数据
 * @param {Array} tableData 表格数据
 * @param {String} xProp X轴字段名
 */
export const handleSingleChart = (option, chartConfig, iconConfig, column, xData, tableData, xProp) => {
  // 修复：优先使用图标配置的 prop，然后是图表配置的 prop
  const dataProp = iconConfig.prop || chartConfig.prop;

  if (!dataProp) {
    return;
  }

  let seriesData;
  
  // 检查是否为计算字段（如差额字段）
  if (dataProp === 'differenceEntry') {
    // 计算入职人数差额
    seriesData = calculateDifferenceSeriesData(tableData, xProp, 'targetEntry', 'actualEntry', xData);
  } else if (dataProp === 'differencePerformance') {
    // 计算业绩差额
    seriesData = calculateDifferenceSeriesData(tableData, xProp, 'targetPerformance', 'actualPerformance', xData);
  } else {
    // 普通字段直接计算
    seriesData = calculateSeriesData(tableData, xProp, dataProp, xData);
  }

  // 修复：使用更合适的默认名称
  const seriesName = chartConfig.name || column.label || dataProp;

  const series = {
    name: seriesName,
    type: 'bar',
    data: seriesData,
    barWidth: '15px',
    itemStyle: { color: chartConfig.color || '#5470C6' },
    label: { show: true, position: 'top' }
  };

  option.series.push(series);
  option.legend.data.push(series.name);
  option.xAxis.data = xData;
};

/**
 * 处理双柱图
 * @param {Object} option 图表选项
 * @param {Object} chartConfig 图表配置
 * @param {Array} xData X轴数据
 * @param {Array} tableData 表格数据
 * @param {String} xProp X轴字段名
 */
export const handleDoubleChart = (option, chartConfig, xData, tableData, xProp) => {
  // 修复：确保 props 存在且长度足够
  if (!chartConfig.props || chartConfig.props.length < 2) {
    return;
  }

  const [prop1, prop2] = chartConfig.props;

  // 计算两个系列的数据
  const seriesData1 = calculateSeriesData(tableData, xProp, prop1, xData);
  const seriesData2 = calculateSeriesData(tableData, xProp, prop2, xData);

  // 获取名称和颜色
  const name1 = chartConfig.names?.[0] || prop1;
  const name2 = chartConfig.names?.[1] || prop2;
  const color1 = chartConfig.colors?.[0] || '#5470C6';
  const color2 = chartConfig.colors?.[1] || '#91CC75';

  // 添加第一个系列
  option.series.push({
    name: name1,
    type: 'bar',
    data: seriesData1,
    barWidth: '15px',
    itemStyle: { color: color1 },
    label: { show: true, position: 'top' }
  });

  // 添加第二个系列
  option.series.push({
    name: name2,
    type: 'bar',
    data: seriesData2,
    barWidth: '15px',
    itemStyle: { color: color2 },
    label: { show: true, position: 'top' }
  });

  // 设置图例和X轴数据
  option.legend.data = [name1, name2];
  option.xAxis.data = xData;

  // 可选：添加差异线
  if (chartConfig.showDifference) {
    const differenceData = seriesData1.map((val, idx) => val - seriesData2[idx]);
    option.series.push({
      name: '差异',
      type: 'line',
      data: differenceData,
      symbol: 'circle',
      symbolSize: 8,
      lineStyle: { width: 2, type: 'dashed' },
      itemStyle: { color: '#EE6666' },
      label: {
        show: true,
        position: 'top',
        formatter: ({ value }) => value > 0 ? `+${value}` : value
      }
    });
    option.legend.data.push('差异');
  }
};

/**
 * 生成图表配置
 * @param {Object} iconConfig 图标配置
 * @param {Object} column 列信息
 * @param {Array} tableData 表格数据
 * @param {Object} bottomIconConfig 底部图标全局配置
 * @returns {Object|null} ECharts 配置对象
 */
export const generateChartOption = (iconConfig, column, tableData, bottomIconConfig) => {
  if (!iconConfig?.chart) return null;

  const globalChartConfig = bottomIconConfig?.chart || {};
  const chartConfig = iconConfig.chart;

  // 1. 获取X轴数据
  const xProp = globalChartConfig?.label || 'projectName';
  const xData = getXAxisData(tableData, xProp);

  // 2. 创建基础图表配置
  const option = createBaseOption(iconConfig, column);

  // 3. 根据图表类型添加数据系列
  try {
    if (chartConfig.type === 'single') {
      handleSingleChart(option, chartConfig, iconConfig, column, xData, tableData, xProp);
    } else if (chartConfig.type === 'double') {
      handleDoubleChart(option, chartConfig, xData, tableData, xProp);
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }

  return option;
};